import { useEffect, useMemo, useState } from 'react';

import { AuthApi, SiteConfigManager } from '@atlas/core';

import { normalizeDepositAmounts } from 'common/utils/depositBonusUtils';
import { paymentTypes, transactionStatuses } from 'src/common/constants';
import { clampAmountToLimits } from 'src/helpers/clampAmountToLimits';

const PAYMENT_HISTORY_ITEMS_COUNT = 30;
const MULTIPLIER = 100;

const getInitialAmount = (paymentMethod, defaultDepositAmount) => {
  if (!paymentMethod) return '';

  const amount =
    Number(defaultDepositAmount) > 0 ? defaultDepositAmount : (paymentMethod.minAmount ?? null);

  return clampAmountToLimits(amount, paymentMethod);
};

export const useInitialPayment = (paymentMethods, currency) => {
  const [completedPaymentMethod, setCompletedPaymentMethod] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [initialValue, setInitialValue] = useState('');
  const depositAmountTemplate = SiteConfigManager.getConfig(
    'config.config.accountProfile.depositAmountTemplate',
    {},
  );

  const defaultDepositAmount = useMemo(() => {
    const depositAmounts = normalizeDepositAmounts(depositAmountTemplate?.[currency]);
    const defaultDeposit = depositAmounts.find(amount => amount?.isDefault === true);

    return defaultDeposit ? defaultDeposit?.value * MULTIPLIER : null;
  }, [currency, depositAmountTemplate]);

  useEffect(() => {
    AuthApi.getClientPaymentsHistory({
      currentSlice: 0,
      itemsPerSlice: PAYMENT_HISTORY_ITEMS_COUNT,
      operation: paymentTypes.DEPOSIT,
    })
      .then(response => {
        const items = response?.items ?? [];
        const successfulPayment = items.find(pm => pm.status === transactionStatuses.SUCCESS) || {};
        setCompletedPaymentMethod(successfulPayment);
      })
      .catch(() => setCompletedPaymentMethod({}));
  }, []);

  useEffect(() => {
    if (paymentMethods.length > 0 && completedPaymentMethod) {
      const foundPaymentMethod = paymentMethods.find(
        pm => pm.id === completedPaymentMethod.paymentMethodId,
      );

      const nextPaymentMethod = foundPaymentMethod || paymentMethods[0];

      setPaymentMethod(nextPaymentMethod);
      setInitialValue(
        getInitialAmount(
          nextPaymentMethod,
          foundPaymentMethod ? completedPaymentMethod?.amount : defaultDepositAmount,
        ),
      );
    }
  }, [paymentMethods, completedPaymentMethod, defaultDepositAmount]);

  return { initialValue, paymentMethod, setPaymentMethod };
};
